import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import { useQuery } from "@tanstack/react-query";

export interface DocVersion {
	id: string;
	content: string;
	version: number | null;
	created_at: string;
	source: string;
}

export function useDocVersionHistory(
	docId: string,
	currentContent: string | null,
) {
	const { data: session } = useSession();

	return useQuery({
		queryKey: ["doc-version-history", docId],
		queryFn: async (): Promise<DocVersion[]> => {
			const { data, error } = await supabase
				.from("audit_log")
				.select("id, created_at, changes, source, metadata")
				.eq("entity_id", docId)
				.eq("entity_type", "library_item")
				.in("action", ["create", "update"])
				.order("created_at", { ascending: true });

			if (error) throw error;

			const versions: DocVersion[] = [];

			for (const entry of data ?? []) {
				const changes = entry.changes as Record<string, unknown> | null;
				if (!changes?.content) continue;
				// CONTEXT: MCP stores changes.content as { old, new } object.
				// Web stores changes.content as a plain string (just the new value).
				const content =
					typeof changes.content === "string"
						? changes.content
						: (changes.content as { new: string }).new;
				const meta = entry.metadata as Record<string, unknown> | null;
				versions.push({
					id: entry.id,
					content,
					version:
						typeof meta?.version === "number" ? meta.version : null,
					created_at: entry.created_at,
					source: entry.source ?? "unknown",
				});
			}

			// Append current live content as the latest "version"
			if (currentContent !== null) {
				versions.push({
					id: "current",
					content: currentContent,
					version: null,
					created_at: new Date().toISOString(),
					source: "live",
				});
			}

			return versions;
		},
		enabled: !!session?.user?.id && !!docId,
	});
}
